import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, UploadSimple, Trash, DownloadSimple, PencilSimple, CaretDown } from "@phosphor-icons/react";

const EMPTY = { first_name: "", last_name: "", birth_date: "", weight: "", belt: "", school: "", height: "", sex: "", disciplines: [] };
const onlyInt = (v) => String(v ?? "").replace(/[^0-9]/g, "");

const BELT_OPTIONS = [
  "Bianca (10 Geup)", "Bianca av. (9 Geup)", "Gialla (8 Geup)", "Gialla av. (7 Geup)",
  "Verde (6 Geup)", "Verde av. (5 Geup)", "Blu (4 Geup)", "Blu av. (3 Geup)",
  "Rossa (2 Geup)", "Rossa av. (1 Geup)", "Nera 1 dan", "Nera 2 dan", "Nera 3 dan",
  "Nera 4 dan", "Nera 5 dan", "Nera 6 dan", "Nera 7 dan", "Nera 8 dan",
];

function BeltCombobox({ value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const ref = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = BELT_OPTIONS.filter((b) => b.toLowerCase().includes((query || "").toLowerCase()));

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Input data-testid="athlete-belt" value={query} placeholder={placeholder} autoComplete="off"
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)} className="pr-8" />
        <CaretDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 pointer-events-none" />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-card shadow-lg max-h-[220px] overflow-y-auto" data-testid="belt-list">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">—</div>
          ) : filtered.map((b) => (
            <button type="button" key={b} data-testid={`belt-opt-${b}`}
              onClick={() => { onChange(b); setQuery(b); setOpen(false); }}
              className={`flex w-full items-center px-3 py-2 text-left text-sm hover:bg-secondary ${value === b ? "bg-secondary font-semibold" : ""}`}>
              {b}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AthletesTab({ eventId, event }) {
  const t = useT();
  const canEdit = ["owner", "co_organizer", "registration"].includes(event.my_role);
  const [athletes, setAthletes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const fileRef = useRef();

  const allowed = event?.type === "mixed" ? ["combat", "poomsae"] : [event?.type].filter(Boolean);
  const singleDiscipline = allowed.length === 1 ? allowed[0] : null;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/athletes`);
      setAthletes(data);
    } catch (e) {
      if (e.response?.status !== 404) toast.error(formatApiError(e.response?.data?.detail));
      setAthletes([]);
    }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const toggleDisc = (d) => {
    if (!allowed.includes(d)) return;
    setForm((f) => ({ ...f, disciplines: f.disciplines.includes(d) ? f.disciplines.filter((x) => x !== d) : [...f.disciplines, d] }));
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...EMPTY, disciplines: singleDiscipline ? [singleDiscipline] : [] });
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({
      first_name: a.first_name || "", last_name: a.last_name || "", birth_date: a.birth_date || "",
      weight: a.weight ?? "", belt: a.belt || "", school: a.school || "", height: a.height ?? "", sex: a.sex || "",
      disciplines: (a.disciplines || []).filter((d) => allowed.includes(d)),
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.first_name || !form.last_name) return toast.error(t("ath.reqNames"));
    const payload = {
      ...form,
      weight: form.weight ? parseInt(form.weight, 10) : null,
      height: form.height ? parseInt(form.height, 10) : null,
      sex: form.sex || null,
      disciplines: singleDiscipline ? [singleDiscipline] : form.disciplines,
    };
    try {
      if (editingId) { await api.put(`/athletes/${editingId}`, payload); toast.success(t("ath.updated")); }
      else { await api.post(`/events/${eventId}/athletes`, payload); toast.success(t("ath.added")); }
      setForm(EMPTY); setEditingId(null); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/athletes/${id}`); load(); }
    catch (e) { if (e.response?.status === 404) load(); else toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/events/${eventId}/athletes/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("ath.imported", { n: data.imported }));
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { e.target.value = ""; }
  };

  const esc = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const CSV_HEADER = () => [t("csv.firstName"), t("csv.lastName"), t("csv.birth"), t("csv.weight"), t("csv.belt"), t("csv.school"), t("csv.height"), t("csv.sex"), t("csv.compType")].join(",");
  const discLabel = (d = []) => (d.includes("combat") && d.includes("poomsae")) ? `${t("type.combat")} + ${t("type.poomsae")}` : d.includes("combat") ? t("type.combat") : d.includes("poomsae") ? t("type.poomsae") : "";

  const downloadTemplate = () => {
    const rows = [
      ["Mario", "Rossi", "2010-05-12", "45", "Gialla", "TKD Kihap", "150", "M", t("type.combat")],
      ["Lucia", "Bianchi", "2008-03-01", "52", "Rossa", "TKD Kihap", "160", "F", `${t("type.poomsae")} + ${t("type.combat")}`],
    ];
    const csv = [CSV_HEADER(), ...rows.map((r) => r.map(esc).join(","))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "template_atleti.csv"; a.click();
  };

  const downloadAthletes = () => {
    const lines = athletes.map((a) => [a.first_name, a.last_name, a.birth_date || "", a.weight ?? "", a.belt || "", a.school || "", a.height ?? "", a.sex || "", discLabel(a.disciplines)].map(esc).join(","));
    const csv = [CSV_HEADER(), ...lines].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "atleti.csv"; a.click();
  };

  const byName = (a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`);
  const females = athletes.filter((a) => a.sex === "F").sort(byName);
  const males = athletes.filter((a) => a.sex === "M").sort(byName);
  const others = athletes.filter((a) => a.sex !== "F" && a.sex !== "M").sort(byName);

  const Rows = ({ list }) => list.map((a) => (
    <TableRow key={a.id} className="border-border" data-testid={`athlete-row-${a.id}`}>
      <TableCell className="font-medium">{a.last_name} {a.first_name}</TableCell>
      <TableCell className="text-muted-foreground">{a.birth_date || "-"}</TableCell>
      <TableCell>{a.weight ? `${a.weight} kg` : "-"}</TableCell>
      <TableCell>{a.belt || "-"}</TableCell>
      <TableCell className="text-muted-foreground">{a.school || "-"}</TableCell>
      <TableCell>{a.height ? `${a.height} cm` : "-"}</TableCell>
      <TableCell>
        <div className="flex gap-1">
          {a.disciplines?.includes("combat") && <Badge className="bg-hong/15 text-hong border-0 text-[10px]">KYORUGI</Badge>}
          {a.disciplines?.includes("poomsae") && <Badge className="bg-chung/15 text-chung border-0 text-[10px]">POOMSAE</Badge>}
        </div>
      </TableCell>
      <TableCell className="text-right">
        {canEdit && <div className="flex items-center justify-end gap-3">
          <button onClick={() => openEdit(a)} className="text-muted-foreground hover:text-brand-yellow" title={t("ath.edit")} data-testid={`edit-athlete-${a.id}`}><PencilSimple size={16} /></button>
          <button onClick={() => remove(a.id)} className="text-muted-foreground hover:text-hong" title={t("common.delete")} data-testid={`delete-athlete-${a.id}`}><Trash size={16} /></button>
        </div>}
      </TableCell>
    </TableRow>
  ));

  const SectionHead = ({ label, n, variant }) => {
    const bar = variant === "pink" ? "bg-pink-400 text-white" : variant === "blue" ? "bg-sky-400 text-white" : "bg-secondary/40 text-muted-foreground";
    return (
      <TableRow className={`hover:bg-transparent border-border ${bar}`}>
        <TableCell colSpan={8} className={`py-1.5 text-xs uppercase tracking-widest font-bold ${bar}`}>{label} ({n})</TableCell>
      </TableRow>
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div className="space-y-3">
          <h2 className="text-2xl font-bold">{t("ath.title")} <span className="text-muted-foreground font-normal text-lg">({athletes.length})</span></h2>
          {canEdit && <Button className="gap-2" onClick={openAdd} data-testid="add-athlete-btn"><Plus weight="bold" /> {t("ath.add")}</Button>}
        </div>
        <div className="flex flex-col items-end gap-2">
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" hidden onChange={onImport} data-testid="import-file-input" />
          {canEdit && <Button variant="outline" onClick={() => fileRef.current?.click()} className="gap-2" data-testid="import-btn"><UploadSimple /> {t("ath.import")}</Button>}
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadTemplate} className="gap-2" data-testid="download-template-btn"><DownloadSimple /> {t("ath.templateCsv")}</Button>
            <Button variant="outline" onClick={downloadAthletes} className="gap-2" data-testid="download-athletes-btn"><DownloadSimple /> {t("ath.exportCsv")}</Button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>{editingId ? t("ath.edit") : t("ath.new")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-2"><Label>{t("ath.firstName")}</Label><Input data-testid="athlete-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
            <div className="space-y-2"><Label>{t("ath.lastName")}</Label><Input data-testid="athlete-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            <div className="space-y-2">
              <Label>{t("ath.sex")}</Label>
              <div className="flex rounded-md border border-border overflow-hidden">
                {["F", "M"].map((s) => (
                  <button key={s} type="button" onClick={() => setForm({ ...form, sex: s })} data-testid={`athlete-sex-${s}`}
                    className={`flex-1 py-2 text-sm font-semibold transition-colors ${form.sex === s ? (s === "F" ? "bg-pink-400 text-white" : "bg-sky-400 text-white") : "text-muted-foreground hover:text-foreground"}`}>{s}</button>
                ))}
              </div>
            </div>
            <div className="space-y-2"><Label>{t("ath.birth")}</Label><Input type="date" data-testid="athlete-birth" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} /></div>
            <div className="space-y-2"><Label>{t("ath.weight")}</Label><Input inputMode="numeric" data-testid="athlete-weight" value={form.weight} onChange={(e) => setForm({ ...form, weight: onlyInt(e.target.value) })} /></div>
            <div className="space-y-2"><Label>{t("ath.height")}</Label><Input inputMode="numeric" data-testid="athlete-height" value={form.height} onChange={(e) => setForm({ ...form, height: onlyInt(e.target.value) })} /></div>
            <div className="space-y-2"><Label>{t("ath.belt")}</Label><BeltCombobox value={form.belt} onChange={(v) => setForm({ ...form, belt: v })} placeholder={t("ath.belt")} /></div>
            <div className="space-y-2"><Label>{t("csv.school")}</Label><Input data-testid="athlete-school" value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} placeholder="Es. TKD Kihap" /></div>
            <div className="col-span-2 space-y-2">
              <Label>{t("ath.disciplines")}</Label>
              <div className="flex gap-6 pt-1">
                {allowed.includes("combat") && (<label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={form.disciplines.includes("combat")} disabled={!!singleDiscipline} onCheckedChange={() => toggleDisc("combat")} data-testid="disc-combat" /> {t("type.combat")}</label>)}
                {allowed.includes("poomsae") && (<label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={form.disciplines.includes("poomsae")} disabled={!!singleDiscipline} onCheckedChange={() => toggleDisc("poomsae")} data-testid="disc-poomsae" /> {t("type.poomsae")}</label>)}
              </div>
              {singleDiscipline && <p className="text-xs text-muted-foreground">{t("ath.lockedDiscipline")}</p>}
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="athlete-save">{t("ath.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-border">
              <TableHead>{t("ath.colName")}</TableHead>
              <TableHead>{t("ath.colBirth")}</TableHead>
              <TableHead>{t("ath.colWeight")}</TableHead>
              <TableHead>{t("ath.colBelt")}</TableHead>
              <TableHead>{t("csv.school")}</TableHead>
              <TableHead>{t("csv.height")}</TableHead>
              <TableHead>{t("ath.colDisc")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {athletes.length === 0 && (<TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">{t("ath.empty")}</TableCell></TableRow>)}
            {females.length > 0 && <SectionHead label={t("ath.female")} n={females.length} variant="pink" />}
            <Rows list={females} />
            {males.length > 0 && <SectionHead label={t("ath.male")} n={males.length} variant="blue" />}
            <Rows list={males} />
            {others.length > 0 && <SectionHead label={t("ath.noSex")} n={others.length} />}
            <Rows list={others} />
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
